import { useEffect, useMemo, useRef, useState } from "react";
import { WORLD_MANIFEST, getPlot } from "@townsy/world";
import { isSameOriginApiImage } from "../../advertise/media";
import { uniformEdgeColor } from "../../scene/media/mediaLoader";
import { SIGN_TEXTURE, buildSignAtlas, centredSquare, type Region, type SignAtlas } from "../../scene/signs/layout";
import { paintBusinessCanvas, paintMessageBoardCanvas, type BusinessSignSpec, type SignImage } from "../../scene/signs/signCanvas";

/**
 * The store screen's live preview (ADR-0020): the rooftop board and the storefront banner exactly as the town paints them
 * (the same canvas painter and layout as the 3D signs, sized from this store's sign anchors), the TV sign inside, and the
 * listing card of the store panel and the directory. Images: only same-origin API images (the owner's private preview of
 * the logo, or the approved public one); nothing else is ever loaded.
 */
export interface StorePreviewModel {
  businessName: string;
  monogram: string;
  brandColor: string;
  accentColor: string;
  /** Same-origin API image URL of the logo (private preview while unpublished), or null for a name-only sign. */
  logoUrl: string | null;
  description: string;
  websiteHost: string | null;
  signTitle: string;
  signMessage: string;
}

const DEFAULT_SKYLINE = { width: 9.6, height: 7.2 };
const DEFAULT_FRONT = { width: 8, height: 2.2 };
const DEFAULT_TV = { width: 3.2, height: 1.9 };

/** The atlas the town builds for business signs (rooftop + storefront + interior slots), from this store's anchors. */
export function previewAtlas(propertyId: string): { atlas: SignAtlas; tvAspect: number } {
  const plot = getPlot(WORLD_MANIFEST, propertyId);
  const sky = plot?.signAnchors.find((s) => s.kind === "skyline") ?? DEFAULT_SKYLINE;
  const front = plot?.signAnchors.find((s) => s.kind === "storefront") ?? DEFAULT_FRONT;
  const room = WORLD_MANIFEST.interiors.find((i) => i.id === "store_room");
  const sign = room?.signAnchor ?? { width: 7, height: 2.2 };
  const tv = room?.boardAnchor ?? DEFAULT_TV;
  const atlas = buildSignAtlas([
    { key: "rooftop", width: sky.width, height: sky.height },
    { key: "storefront", width: front.width, height: front.height },
    { key: "interior", width: sign.width, height: sign.height },
  ]);
  return { atlas, tvAspect: tv.width / tv.height };
}

/** Load a same-origin logo for the painter (with its corner colour, as the town's loader samples it). */
function useLogoImage(url: string | null): SignImage | null {
  const [image, setImage] = useState<{ url: string; image: SignImage } | null>(null);
  useEffect(() => {
    if (!url || !isSameOriginApiImage(url) || typeof Image === "undefined") return;
    let live = true;
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      if (!live) return;
      let edgeColor: string | null = null;
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const w = c.width - 1;
          const h = c.height - 1;
          const px = [ctx.getImageData(0, 0, 1, 1).data, ctx.getImageData(w, 0, 1, 1).data, ctx.getImageData(0, h, 1, 1).data, ctx.getImageData(w, h, 1, 1).data];
          edgeColor = uniformEdgeColor(px.flatMap((d) => Array.from(d)));
        }
      } catch {
        edgeColor = null;
      }
      setImage({ url, image: { source: img, width: img.naturalWidth, height: img.naturalHeight, edgeColor } });
    };
    img.onerror = () => live && setImage(null);
    img.src = url;
    return () => {
      live = false;
    };
  }, [url]);
  return image && image.url === url ? image.image : null;
}

function drawRegion(target: HTMLCanvasElement | null, source: HTMLCanvasElement, r: Region): void {
  if (!target) return;
  const ctx = target.getContext("2d");
  if (!ctx) return;
  target.width = Math.round(r.w);
  target.height = Math.round(r.h);
  ctx.drawImage(source, r.x, r.y, r.w, r.h, 0, 0, target.width, target.height);
}

export function StorePreview({ propertyId, model, address }: { propertyId: string; model: StorePreviewModel; address: string }) {
  const { atlas, tvAspect } = useMemo(() => previewAtlas(propertyId), [propertyId]);
  const logo = useLogoImage(model.logoUrl);
  const rooftopRef = useRef<HTMLCanvasElement>(null);
  const bannerRef = useRef<HTMLCanvasElement>(null);
  const tvRef = useRef<HTMLCanvasElement>(null);
  const hasLogo = Boolean(model.logoUrl);
  const name = model.businessName.trim() || "Your business name";

  useEffect(() => {
    if (typeof document === "undefined") return;
    const off = document.createElement("canvas");
    off.width = SIGN_TEXTURE.width;
    off.height = SIGN_TEXTURE.height;
    const ctx = off.getContext("2d");
    if (!ctx) return;
    // Exactly what the town paints: a store with a logo has a square rooftop board (also while the logo loads).
    const spec: BusinessSignSpec = { businessName: name, monogram: model.monogram, brandColor: model.brandColor, accentColor: model.accentColor, squareRooftop: hasLogo, logo };
    paintBusinessCanvas(ctx, spec, atlas);
    const roof = atlas.slots.find((s) => s.key === "rooftop");
    const front = atlas.slots.find((s) => s.key === "storefront");
    if (roof) drawRegion(rooftopRef.current, off, hasLogo ? centredSquare(roof.region) : roof.region);
    if (front) drawRegion(bannerRef.current, off, front.region);
  }, [atlas, name, model.monogram, model.brandColor, model.accentColor, hasLogo, logo]);

  useEffect(() => {
    const c = tvRef.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    c.width = 640;
    c.height = Math.round(640 / tvAspect);
    const title = model.signTitle.trim() || (model.signMessage.trim() ? name : "");
    paintMessageBoardCanvas(ctx, c.width, c.height, title ? { title, body: model.signMessage.trim() } : null, "tv");
  }, [tvAspect, model.signTitle, model.signMessage, name]);

  return (
    <div className="ss-preview" data-testid="store-setup-preview">
      <figure className="ss-stage" aria-label={`Preview of the store at ${address}: the rooftop sign and the storefront banner`}>
        <div className={`ss-roof-board${hasLogo ? " is-square" : ""}`}>
          <canvas ref={rooftopRef} className="ss-canvas" data-testid="store-preview-rooftop" aria-hidden="true" />
          <span className="ss-post ss-post-l" aria-hidden="true" />
          <span className="ss-post ss-post-r" aria-hidden="true" />
        </div>
        <div className="ss-facade" aria-hidden="true">
          <canvas ref={bannerRef} className="ss-canvas ss-banner" data-testid="store-preview-banner" />
          <div className="ss-front">
            <span className="ss-window" />
            <span className="ss-door" />
            <span className="ss-window" />
          </div>
        </div>
        <div className="ss-street" aria-hidden="true" />
        <figcaption className="ss-caption">{address}</figcaption>
      </figure>

      <div className="ss-preview-row">
        <div className="ss-tv-wrap">
          <div className="ss-mini-title">The sign inside</div>
          <div className="ss-tv">
            <canvas ref={tvRef} className="ss-canvas" data-testid="store-preview-tv" aria-label="Preview of the TV sign inside your store" role="img" />
          </div>
        </div>
        <div className="ss-card-wrap">
          <div className="ss-mini-title">In the store panel and directory</div>
          <div className="ss-card" data-testid="store-preview-card">
            <PreviewLogoTile model={model} name={name} />
            <div className="ss-card-text">
              <strong className="ss-card-name">{name}</strong>
              {model.description.trim() ? <span className="ss-card-desc">{model.description.trim()}</span> : <span className="ss-card-desc muted">Your short description shows here.</span>}
              <span className="ss-card-site">{model.websiteHost ? `Visit website · ${model.websiteHost}` : "No website link"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewLogoTile({ model, name }: { model: StorePreviewModel; name: string }) {
  const [failed, setFailed] = useState<string | null>(null);
  const url = model.logoUrl && isSameOriginApiImage(model.logoUrl) && failed !== model.logoUrl ? model.logoUrl : null;
  if (url) {
    return (
      <span className="ss-card-logo is-image" role="img" aria-label={`${name} logo`}>
        <img src={url} alt="" draggable={false} onError={() => setFailed(url)} />
      </span>
    );
  }
  return (
    <span className="ss-card-logo" style={{ background: model.brandColor }} role="img" aria-label={`${name} logo`}>
      {model.monogram}
    </span>
  );
}
